// /var/www/oszczedzam/client/src/components/transactions/TransactionFilters.js
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, Paper, Typography, TextField, Grid, Button, 
  FormControl, InputLabel, Select, MenuItem, Chip,
  IconButton, Autocomplete, Collapse
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { pl } from 'date-fns/locale';
import { 
  Close as CloseIcon, 
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';

// Kategorie wydatków i przychodów
const allCategories = [
  'Mieszkanie', 'Transport', 'Żywność', 'Rozrywka', 'Zdrowie', 
  'Odzież', 'Edukacja', 'Prezenty', 'Podróże', 'Dzieci', 'Sport', 
  'Zwierzęta', 'Elektronika', 'Rachunki', 'Subskrypcje', 'Inne',
  'Wynagrodzenie', 'Inwestycje', 'Premia', 'Sprzedaż'
];

// Hook dla debounce
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

function TransactionFilters({ onApplyFilters, availableTags = [] }) {
  const [showFilters, setShowFilters] = useState(false);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [category, setCategory] = useState('');
  const [transactionType, setTransactionType] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [isRecurring, setIsRecurring] = useState('');
  const [titleFilter, setTitleFilter] = useState('');
  
  // Debounced wartość dla wyszukiwania w tytule
  const debouncedTitleFilter = useDebounce(titleFilter, 300);

  // Sprawdź czy jakieś filtry są aktywne
  const hasActiveFilters = useCallback(() => {
    return !!(dateFrom || dateTo || category || transactionType || 
             selectedTags.length > 0 || amountMin || amountMax || 
             isRecurring || titleFilter);
  }, [dateFrom, dateTo, category, transactionType, selectedTags, 
      amountMin, amountMax, isRecurring, titleFilter]);

  // Automatyczne zastosowanie filtrów przy zmianie wartości
  useEffect(() => {
    const filters = {
      dateFrom: dateFrom ? dateFrom.toISOString().split('T')[0] : null,
      dateTo: dateTo ? dateTo.toISOString().split('T')[0] : null,
      category: category || null,
      transactionType: transactionType || null,
      tags: selectedTags.length > 0 ? selectedTags : null,
      amountMin: amountMin ? parseFloat(amountMin) : null,
      amountMax: amountMax ? parseFloat(amountMax) : null,
      isRecurring: isRecurring === 'yes' ? true : isRecurring === 'no' ? false : null,
      title: debouncedTitleFilter || null
    };
    
    onApplyFilters(filters);
  }, [dateFrom, dateTo, category, transactionType, selectedTags, 
      amountMin, amountMax, isRecurring, debouncedTitleFilter, onApplyFilters]);

  // Resetuj filtry
  const handleResetFilters = () => {
    setDateFrom(null);
    setDateTo(null);
    setCategory('');
    setTransactionType('');
    setSelectedTags([]);
    setAmountMin('');
    setAmountMax('');
    setIsRecurring('');
    setTitleFilter('');
  };

  // Przełącz widoczność filtrów
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  // Funkcje pomocnicze dla szybkich filtrów
  const setQuickFilter = (filterType, value) => {
    switch (filterType) {
      case 'thisMonth':
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        setDateFrom(firstDay);
        setDateTo(lastDay);
        break;
      case 'lastMonth':
        const lastMonth = new Date();
        lastMonth.setMonth(lastMonth.getMonth() - 1);
        const firstDayLast = new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 1);
        const lastDayLast = new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0);
        setDateFrom(firstDayLast);
        setDateTo(lastDayLast);
        break;
      case 'last7Days':
        const today = new Date();
        const weekAgo = new Date(today);
        weekAgo.setDate(today.getDate() - 7);
        setDateFrom(weekAgo);
        setDateTo(today);
        break;
      case 'expenses':
        setTransactionType('expense');
        break;
      case 'income':
        setTransactionType('income');
        break;
      default:
        break;
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">
            Filtry
            {hasActiveFilters() && (
              <Chip 
                label="Aktywne" 
                size="small" 
                color="primary" 
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
          
          {/* Szybkie filtry */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip 
              label="Ten miesiąc" 
              variant="outlined" 
              size="small"
              onClick={() => setQuickFilter('thisMonth')}
              clickable
            />
            <Chip 
              label="Ostatni miesiąc" 
              variant="outlined" 
              size="small"
              onClick={() => setQuickFilter('lastMonth')}
              clickable
            />
            <Chip 
              label="Ostatnie 7 dni" 
              variant="outlined" 
              size="small"
              onClick={() => setQuickFilter('last7Days')}
              clickable
            />
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          {hasActiveFilters() && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleResetFilters}
              startIcon={<CloseIcon />}
            >
              Resetuj
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={toggleFilters}
          >
            {showFilters ? 'Ukryj filtry' : 'Więcej filtrów'}
          </Button>
        </Box>
      </Box>
      
      {/* Podstawowe filtry - zawsze widoczne */}
      <Box sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          {/* Filtrowanie po tytule - zawsze widoczne */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Szukaj w tytule lub opisie"
              value={titleFilter}
              onChange={(e) => setTitleFilter(e.target.value)}
              placeholder="Wpisz, aby wyszukać..."
            />
          </Grid>
          
          {/* Typ transakcji */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="type-filter-label">Typ transakcji</InputLabel>
              <Select
                labelId="type-filter-label"
                id="type-filter"
                value={transactionType}
                label="Typ transakcji"
                onChange={(e) => setTransactionType(e.target.value)}
              >
                <MenuItem value="">Wszystkie</MenuItem>
                <MenuItem value="income">Przychody</MenuItem>
                <MenuItem value="expense">Wydatki</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Kategoria */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="category-filter-label">Kategoria</InputLabel>
              <Select
                labelId="category-filter-label"
                id="category-filter"
                value={category}
                label="Kategoria"
                onChange={(e) => setCategory(e.target.value)}
              >
                <MenuItem value="">Wszystkie</MenuItem>
                {allCategories.map((cat) => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>
      
      {/* Rozszerzone filtry */}
      <Collapse in={showFilters}>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {/* Zakres dat */}
            <Grid item xs={12} sm={6} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
                <DatePicker
                  label="Data od"
                  value={dateFrom}
                  onChange={setDateFrom}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small"
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
                <DatePicker
                  label="Data do"
                  value={dateTo}
                  onChange={setDateTo}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small"
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            
            {/* Zakres kwoty */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Kwota min"
                type="number"
                value={amountMin}
                onChange={(e) => setAmountMin(e.target.value)}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Kwota max"
                type="number"
                value={amountMax}
                onChange={(e) => setAmountMax(e.target.value)}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            
            {/* Cykliczność */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="recurring-filter-label">Cykliczność</InputLabel>
                <Select
                  labelId="recurring-filter-label"
                  id="recurring-filter"
                  value={isRecurring}
                  label="Cykliczność"
                  onChange={(e) => setIsRecurring(e.target.value)}
                >
                  <MenuItem value="">Wszystkie</MenuItem>
                  <MenuItem value="yes">Tylko cykliczne</MenuItem>
                  <MenuItem value="no">Tylko jednorazowe</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Tagi */}
            <Grid item xs={12} sm={6} md={9}>
              <Autocomplete
                multiple
                id="tags-filter"
                options={availableTags}
                value={selectedTags}
                onChange={(event, newValue) => {
                  setSelectedTags(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Tagi"
                    size="small"
                    placeholder="Wybierz tagi..."
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip 
                      variant="outlined" 
                      label={option} 
                      {...getTagProps({ index })} 
                      key={option}
                      size="small"
                    />
                  ))
                }
                size="small"
                ChipProps={{ size: 'small' }}
              />
            </Grid>
          </Grid>
        </Box>
      </Collapse>
      
      {/* Wyświetl aktywne filtry */}
      {hasActiveFilters() && (
        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" gutterBottom color="text.secondary">
            Aktywne filtry:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {titleFilter && (
              <Chip 
                label={`Szukaj: "${titleFilter}"`} 
                size="small" 
                onDelete={() => setTitleFilter('')}
                color="primary"
                variant="outlined"
              />
            )}
            {dateFrom && (
              <Chip 
                label={`Od: ${dateFrom.toLocaleDateString('pl-PL')}`} 
                size="small" 
                onDelete={() => setDateFrom(null)}
                color="primary"
                variant="outlined"
              />
            )}
            {dateTo && (
              <Chip 
                label={`Do: ${dateTo.toLocaleDateString('pl-PL')}`} 
                size="small" 
                onDelete={() => setDateTo(null)}
                color="primary"
                variant="outlined"
              />
            )}
            {category && (
              <Chip 
                label={`Kategoria: ${category}`} 
                size="small" 
                onDelete={() => setCategory('')}
                color="primary"
                variant="outlined"
              />
            )}
            {transactionType && (
              <Chip 
                label={`Typ: ${transactionType === 'income' ? 'Przychody' : 'Wydatki'}`} 
                size="small" 
                onDelete={() => setTransactionType('')}
                color="primary"
                variant="outlined"
              />
            )}
            {amountMin && (
              <Chip 
                label={`Min: ${amountMin} zł`} 
                size="small" 
                onDelete={() => setAmountMin('')}
                color="primary"
                variant="outlined"
              />
            )}
            {amountMax && (
              <Chip 
                label={`Max: ${amountMax} zł`} 
                size="small" 
                onDelete={() => setAmountMax('')}
                color="primary"
                variant="outlined"
              />
            )}
            {isRecurring && (
              <Chip 
                label={`${isRecurring === 'yes' ? 'Cykliczne' : 'Jednorazowe'}`} 
                size="small" 
                onDelete={() => setIsRecurring('')}
                color="primary"
                variant="outlined"
              />
            )}
            {selectedTags.map(tag => (
              <Chip 
                key={tag}
                label={`Tag: ${tag}`} 
                size="small" 
                onDelete={() => setSelectedTags(prev => prev.filter(t => t !== tag))}
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>
        </Box>
      )}
    </Paper>
  );
}

export default TransactionFilters;